import {Box, Input, Textarea, VStack} from '@chakra-ui/react'
import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import FormControlledInput from '../../Form/FormControlledInput'
import SkillOptionBar from '../SkillOptionBar'
import {ProductDescriptionSkillFormData} from '../../../dataModels/SkillFormData'
import {SKILL_PRODUCT_DESCRIPTION_EXAMPLES} from '../../../constants/skillExamples'
import {useIsMobile} from '../../../hooks/useIsMobile'
import {COOKIE_INPUTS} from '../../../constants/cookies'
import {deleteCookieClient} from '../../../utils/cookieHelpers'
import {parseCookies} from 'nookies'
import useTranslation from 'next-translate/useTranslation'
import {getRandomIndex} from '../../../utils/randomHelpers'
import {useRouter} from 'next/router'

interface Props {
  onGenerating: (values: ProductDescriptionSkillFormData) => void
  setOutput: (output: string) => void
}

const SkillProductDescriptionForm = ({onGenerating, setOutput}: Props) => {
  const {t} = useTranslation('skill-product-description')
  const selectedLanguage = useRouter().locale ?? 'en'

  const [randomExampleIndex, setRandomExampleIndex] = useState<number>(0)

  const getRandomExample = () => {
    setRandomExampleIndex(getRandomIndex(SKILL_PRODUCT_DESCRIPTION_EXAMPLES, randomExampleIndex))
    const example = SKILL_PRODUCT_DESCRIPTION_EXAMPLES[randomExampleIndex]
    setOutput('')
    setExample(example)
  }

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: {errors},
  } = useForm<ProductDescriptionSkillFormData>()

  const onSubmit = async (values: any) => {
    onGenerating(values)
  }
  const [example, setExample] = useState<ProductDescriptionSkillFormData | null>()
  const isMobile = useIsMobile()
  const direction = isMobile ? 'column' : 'row'

  useEffect(() => {
    if (!example) return
    setValue('productName', t(`examples${selectedLanguage.toUpperCase()}:${example.productName}`))
    setValue('productType', t(`examples${selectedLanguage.toUpperCase()}:${example.productType}`))
    setValue(
      'productAudience',
      example.productAudience
        ? t(`examples${selectedLanguage.toUpperCase()}:${example.productAudience}`)
        : ''
    )
    setValue(
      'productCharacteristic',
      t(`examples${selectedLanguage.toUpperCase()}:${example.productCharacteristic}`)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [example])

  useEffect(() => {
    const {presentInputs} = parseCookies()
    if (presentInputs) {
      const presentPlaygroundValues = JSON.parse(presentInputs) as ProductDescriptionSkillFormData
      setValue('productName', presentPlaygroundValues.productName)
      setValue('productType', presentPlaygroundValues.productType)
      setValue('productAudience', presentPlaygroundValues.productAudience)
      setValue('productCharacteristic', presentPlaygroundValues.productCharacteristic)
    }
    deleteCookieClient(COOKIE_INPUTS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{width: '100%'}}>
      <VStack p={isMobile ? 0 : 5} spacing={6} w='full' borderRadius='medium' placeItems='center'>
        <FormControlledInput
          label={t('skill_product_description_product_name_input')}
          error={'productName' in errors}
          errorMessage={errors.productName?.message}
          required
          direction={direction}>
          <Input
            variant='playground'
            w='full'
            placeholder='Smoke EPX6'
            {...register('productName', {
              required: t('common:input_required'),
              maxLength: {value: 100, message: t('common:input_too_long')},
            })}
          />
        </FormControlledInput>

        <FormControlledInput
          label={t('skill_product_description_product_type_input')}
          error={'productType' in errors}
          errorMessage={errors.productType?.message}
          required
          direction={direction}>
          <Input
            variant='playground'
            w='full'
            placeholder='Barbecue'
            {...register('productType', {
              required: t('common:input_required'),
              maxLength: {value: 100, message: t('common:input_too_long')},
            })}
          />
        </FormControlledInput>

        <FormControlledInput
          label={t('skill_product_description_product_audience_input')}
          error={'productAudience' in errors}
          errorMessage={errors.productAudience?.message}
          direction={direction}>
          <Input
            variant='playground'
            w='full'
            placeholder='Homme de 25 à 45 ans qui passent du temps à...'
            {...register('productAudience', {
              maxLength: {value: 150, message: t('common:input_too_long')},
            })}
          />
        </FormControlledInput>

        <FormControlledInput
          label={t('skill_product_description_product_characteristic_input')}
          error={'productCharacteristic' in errors}
          errorMessage={errors.productCharacteristic?.message}
          required
          direction='column'>
          <Box pt={3} w='full'>
            <Textarea
              resize='none'
              variant='playground'
              w='full'
              minH='200px'
              pt={3}
              placeholder='Barbecue à granulés, température de 95 à 315 °C, rétention de chaleur avec...'
              {...register('productCharacteristic', {
                required: t('common:input_required'),
                maxLength: {value: 1000, message: t('common:input_too_long')},
              })}
            />
          </Box>
        </FormControlledInput>
        <SkillOptionBar
          getRandomExample={getRandomExample}
          onDelete={() => {
            reset()
            setOutput('')
          }}
        />
      </VStack>
    </form>
  )
}

export default SkillProductDescriptionForm
